import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { QuoteService } from '../../quote.service';
import {
  isInterceptorHandledError,
  parseApiError,
} from '../../quotes-api-error';
import {
  ExchangeRateOption,
  ExchangeRatesResponse,
} from '../../../models/exchange-rates.model';

export interface CurrencyRateOption {
  code: string;
  name: string;
  exchange_rate: string;
}

@Component({
  selector: 'app-currency-exchange-edit',
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
    SelectModule,
  ],
  templateUrl: './currency-exchange-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyExchangeEditComponent {
  private quoteService = inject(QuoteService);

  visible = signal(false);
  isLoading = signal(false);
  isSaving = signal(false);
  loadError = signal<string | null>(null);
  saveError = signal<string | null>(null);

  baseCurrencyCode = signal('');
  rateDate = signal<string | null>(null);
  currencyOptions = signal<CurrencyRateOption[]>([]);
  selectedCurrency = signal<CurrencyRateOption | null>(null);
  exchangeRate = signal<number | null>(null);

  async open(): Promise<void> {
    this.visible.set(true);
    this.loadError.set(null);
    this.saveError.set(null);
    this.isLoading.set(true);
    this.currencyOptions.set([]);
    this.selectedCurrency.set(null);
    this.exchangeRate.set(null);
    this.rateDate.set(null);

    try {
      const revision = this.quoteService.revision;
      this.baseCurrencyCode.set(revision.base_currency_code);
      const ratesResponse = await this.quoteService.getExchangeRates(
        revision.base_currency_code,
      );
      this.applyRatesResponse(ratesResponse, revision.currency_code);
      const currentRate = Number(revision.exchange_rate);
      if (!Number.isNaN(currentRate) && currentRate > 0) {
        this.exchangeRate.set(currentRate);
      }
    } catch {
      this.loadError.set(
        'Could not load exchange rates from the European Central Bank. Try again later.',
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  close(): void {
    if (this.isSaving()) {
      return;
    }
    this.visible.set(false);
  }

  onCurrencyChange(option: CurrencyRateOption | null): void {
    this.selectedCurrency.set(option);
    if (option) {
      const rate = Number(option.exchange_rate);
      this.exchangeRate.set(Number.isNaN(rate) ? null : rate);
    }
  }

  get canSave(): boolean {
    const rate = this.exchangeRate();
    return (
      !!this.selectedCurrency() &&
      rate != null &&
      rate > 0 &&
      !this.isLoading() &&
      !this.isSaving() &&
      !this.loadError()
    );
  }

  async save(): Promise<void> {
    const selected = this.selectedCurrency();
    const rate = this.exchangeRate();
    if (!selected || rate == null || rate <= 0) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(null);
    try {
      await this.quoteService.patchRevision({
        currency_code: selected.code,
        exchange_rate: rate.toFixed(6),
      });
      this.visible.set(false);
    } catch (err: unknown) {
      if (!isInterceptorHandledError(err)) {
        this.saveError.set(parseApiError(err));
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  private applyRatesResponse(
    response: ExchangeRatesResponse,
    currentCurrencyCode: string,
  ): void {
    this.baseCurrencyCode.set(response.base_currency_code);
    this.rateDate.set(response.rate_date);

    const displayNames = new Intl.DisplayNames(['en'], { type: 'currency' });
    const options = response.rates.map((rate) =>
      this.toCurrencyOption(rate, displayNames),
    );
    this.currencyOptions.set(options);

    const match =
      options.find((option) => option.code === currentCurrencyCode) ?? null;
    this.selectedCurrency.set(match);
    if (match) {
      const rate = Number(match.exchange_rate);
      this.exchangeRate.set(Number.isNaN(rate) ? null : rate);
    }
  }

  private toCurrencyOption(
    rate: ExchangeRateOption,
    displayNames: Intl.DisplayNames,
  ): CurrencyRateOption {
    let currencyName = rate.currency_code;
    try {
      currencyName = displayNames.of(rate.currency_code) ?? rate.currency_code;
    } catch {
      // Unknown currency codes fall back to the code alone.
    }
    return {
      code: rate.currency_code,
      name: `${currencyName} (${rate.currency_code})`,
      exchange_rate: rate.exchange_rate,
    };
  }
}
